import { useEffect, useRef } from 'react';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import 'videojs-contrib-quality-levels';
import 'videojs-mux';
//require('@/js/shared/utils/video-offset.js');
import '@/lib/utils/video-offset';

const qualityLogger = (player: VideoJsPlayer): void => {
  // @ts-expect-error: Wrong typings
  const qualityLevels = player.qualityLevels();
  qualityLevels.on('change', () => {
    console.log('Quality Level changed!');
    console.log('New level:', qualityLevels[qualityLevels.selectedIndex]);
  });
};

const defaultOptions: VideoJsPlayerOptions = {
  autoplay: false,
  controls: true,
  fluid: false,
  liveui: false,
  preload: 'none',
  responsive: false,
  html5: {
    hlsjsConfig: {
      liveSyncDurationCount: 3,
    },
    vhs: {
      limitRenditionByPlayerDimensions: false,
      useDevicePixelRatio: true,
      overrideNative: !videojs.browser.IS_SAFARI,
      bandwidth: 10000000,
      experimentalBufferBasedABR: true,
    },
  },
  userActions: {
    doubleClick: () => false,
  },
};

export const useVideoJs = (
  options: VideoJsPlayerOptions & {
    liveTracker?: { trackingThreshold: number };
  },
  onReady?: (player: VideoJsPlayer) => any
) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const playerRef = useRef<VideoJsPlayer | null>(null);

  const onPlayerReady = () => {
    const player = playerRef.current as VideoJsPlayer;

    if (player != null) {
      player.on('ended', () => console.log('video ended!'));

      player.on('error', (error: any) => console.log('Videojs error', error));

      if (player.options_.liveui) {
        player.one('play', () => {
          player.on('play', () => player.liveTracker.seekToLiveEdge());
        });
      }
      // Do NOT set player.src() here - video.js would load from initial options
      // and we'd set again, cancelling the request. Source is set only in the effect below.

      qualityLogger(player);
      onReady && onReady(player);
    }
  };

  const playerDestroy = () => {
    if (playerRef.current === null) return;

    playerRef.current.reset();
    playerRef.current = null;
  };

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) return;

    if (playerRef.current == null) {
      // Omit sources so video.js doesn't load on init (avoids cancel when we set src in effect)
      const { sources: _sources, ...restOptions } = options;
      const mergedOptions = {
        ...defaultOptions,
        ...restOptions,
      };

      playerRef.current = videojs(videoElement, mergedOptions, onPlayerReady);
    }
  }, [videoRef]);

  // Single place that sets source. Defer by one frame so React Strict Mode's
  // unmount/remount doesn't abort the first request (avoids 1 cancel in dev).
  const sourceSrc = options.sources?.[0]?.src;
  useEffect(() => {
    const player = playerRef.current;
    if (player && options.sources?.length && sourceSrc) {
      player.src(options.sources as videojs.Tech.SourceObject[]);
    }
  }, [sourceSrc]);

  useEffect(() => {
    return playerDestroy;
  }, [playerRef]);

  return {
    videoRef,
    playerRef,
    playerDestroy,
  };
};
